import { siteImageUrl } from '@orcar/common-d2c';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useHomeBanners } from '@/hooks/useHomeImages';

/**
 * 제주로 특징을 사진으로 보여주는 홈 슬라이드 배너(지도 위, 특징 카드 상단).
 * 회사가 ERP 자사몰관리 > 사이트 내용에서 고친다. 2장 이상이면 자동재생 + 인디케이터가 붙는다.
 */
const AUTOPLAY_MS = 6000;

type HomeFeatureBannerProps = {
  isMobile: boolean;
};

const SWIPE_THRESHOLD = 40;

const HomeFeatureBanner = ({ isMobile }: HomeFeatureBannerProps) => {
  const { list, items: slides } = useHomeBanners();
  const [selected, setIndex] = useState(0);
  const multiple = slides.length > 1;

  const total = slides.length;
  // 게시본이 바뀌어 장 수가 줄면 첫 장부터 보여 준다.
  const index = selected < total ? selected : 0;
  const goTo = (next: number) => setIndex((next + total) % total);
  const goNext = () => setIndex((prev) => (prev + 1) % total);
  const goPrev = () => setIndex((prev) => (prev - 1 + total) % total);

  // 자동재생 — 수동으로 넘기면(index 변경) 타이머가 리셋된다.
  useEffect(() => {
    if (!multiple) return;
    const timer = setTimeout(goNext, AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [index, multiple]);

  // 터치 스와이프로 페이지 넘기기
  const touchStartX = useRef<number | null>(null);
  const onTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };
  const onTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
    if (deltaX < 0) goNext();
    else goPrev();
  };

  const slide = slides[index];
  // 내용을 받는 동안은 자리만 잡아 두고, 보일 배너가 없으면 통째로 뺀다.
  if (!slide) return list ? null : <Banner isMobile={isMobile} />;

  return (
    <Banner
      isMobile={isMobile}
      onTouchStart={multiple ? onTouchStart : undefined}
      onTouchEnd={multiple ? onTouchEnd : undefined}
    >
      {slides.map((item, itemIndex) => (
        <Slide key={item.id} $active={itemIndex === index}>
          <Img
            src={
              isMobile
                ? siteImageUrl(item.mobileImage || item.image, 1080)
                : siteImageUrl(item.image, 1920)
            }
            alt={`${item.eyebrow} ${item.title}`}
            loading='lazy'
          />
        </Slide>
      ))}
      <Scrim />
      <Overlay>
        <Eyebrow>{slide.eyebrow}</Eyebrow>
        <Title>{slide.title}</Title>
      </Overlay>

      {multiple && (
        <>
          <Arrow
            type='button'
            $side='left'
            aria-label='이전 배너'
            onClick={goPrev}
          >
            ‹
          </Arrow>
          <Arrow
            type='button'
            $side='right'
            aria-label='다음 배너'
            onClick={goNext}
          >
            ›
          </Arrow>
          <Dots>
            {slides.map((item, itemIndex) => (
              <Dot
                key={item.id}
                type='button'
                aria-label={`${itemIndex + 1}번 배너`}
                $active={itemIndex === index}
                onClick={() => goTo(itemIndex)}
              />
            ))}
          </Dots>
        </>
      )}
    </Banner>
  );
};

export default HomeFeatureBanner;

const Banner = styled.div<{ isMobile: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ isMobile }) => (isMobile ? '1 / 1' : '2048 / 768')};
  border-radius: ${({ isMobile }) => (isMobile ? '16px' : '20px')};
  overflow: hidden;
  background: ${({ theme }) => theme.COLOR.GREY_BG};
`;

const Slide = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.6s ease;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  /* 사진 위 텍스트 가독성용 어두운 그라데이션 */
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.14) 0%,
    rgba(0, 0, 0, 0.28) 55%,
    rgba(0, 0, 0, 0.42) 100%
  );
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  color: #fff;
  padding: 0 24px;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
`;

const Eyebrow = styled.span`
  font-size: clamp(13px, 2.4vw, 17px);
  font-weight: 600;
  letter-spacing: 0.02em;
  opacity: 0.95;
`;

const Title = styled.strong`
  font-size: clamp(24px, 5vw, 40px);
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const Dots = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 16px;
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '22px' : '8px')};
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition:
    width 0.25s ease,
    background 0.25s ease;
  ${({ $active }) =>
    $active
      ? css`
          background: #fff;
        `
      : css`
          background: rgba(255, 255, 255, 0.55);
        `}
`;

const Arrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $side }) => ($side === 'left' ? 'left: 12px;' : 'right: 12px;')}
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  padding: 0 0 3px;
  border: none;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }
`;
